<template>
  <div class="app-publication-ai-outline page-main">
    <div class="app-publication-ai-outline__header">
      <div class="app-tab-navigation">
        <div v-if="!isIntegrated" class="app-tab-navigation__breadcrumbs">
            <a class="app-tab-navigation__breadcrumb" :href="`/clients/${project.client.id}`">
              <svg class="element-icon--breadcrumb element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-breadcrumb"></use></svg>
              {{ project.client.name }}
            </a>
            <svg class="element-icon--chevron-right element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-chevron-right"></use></svg>
            <a class="app-tab-navigation__breadcrumb" :href="`/projects/${project.id}/courses`">
              <svg class="element-icon--breadcrumb element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-breadcrumb"></use></svg>
              {{ project.name }}
            </a>
            <svg class="element-icon--chevron-right element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-chevron-right"></use></svg>
            <div class="app-tab-navigation__breadcrumb is--active">{{ statusIsCompleted ? 'Course' : 'Outline' }}</div>
        </div>
        <div class="app-tab-navigation__titles">
          <h3 class="app-tab-navigation__title">{{ details.name }}
            <span v-if="!statusIsCompleted" class="element-badge is--alt">Outline Complete</span>
            <span v-if="statusIsCompleted" class="element-badge is--success">Complete</span>
          </h3>
          <p class="app-tab-navigation__supporting-text">Last Modified: {{ formatJsDate(project.updated_at) }}</p>
        </div>
        <button
          @click="toggleInfo()"
          class="element-chip--viewers element-chip is--info">
          Viewers <span :class="['element-chip__trailing', userCount > 1 ? 'is--error' : 'is--alt']">{{ userCount }}</span>
          <svg class="element-icon--chevron-down element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-chevron-down"></use></svg>
        </button>
        <div :class="['app-dropdown--viewers app-dropdown', { 'is--open': showInfo }]">
          <div class="app-dropdown__column">
            <h4 class="app-dropdown__title">Current Viewers</h4>
            <div class="app-dropdown__body">
              <ul>
                <li v-for="user in users"
                  :key="`user-${user.id}`"
                  class="user-list-item">
                  {{ user.name }}
                  <span v-if="user.id === currentUser.id">(You)</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div class="app-tab-navigation__buttons">
          <app-button v-if="!statusIsCompleted"
              @click="onDownloadPressed()"
              class="element-button--secondary element-button">
              Download Outline
          </app-button>
          <app-button v-if="getStatus() === 'completed'"
              @click="onPreviewPressed()"
              class="element-button--secondary element-button">
              Preview
          </app-button>
          <app-button
            class="element-button-primary element-button"
            theme="primary"
            :disabled="disabled || !hasChanges"
            @click="saveOutline()">
            Save
          </app-button>
          <template v-if="canSync">
            <div
            class="app-publication-ai-outline__sync-status">
              <span v-if="isSyncing"
                  title="Sync in progress..."
                  class="element-badge is--processing has--circle">
                  Syncing
              </span>
              <span v-else-if="isSourceOutdated"
                  :title="`Last Sync: ${localizeDate(source.synced_at).format('dddd, MMMM Do YYYY, h:mm:ss a')}`"
                  class="element-badge is--error has--circle">
                  Portal Out of Sync
              </span>
              <span v-else-if="source.synced_at"
                  class="element-badge is--active has--circle">
                  Portal In Sync
              </span>
            </div>
            <button
              type="button"
              class="element-button element-button--primary"
              :disabled="syncStatus || isSyncing"
              @click="onSyncPressed()">
              {{ syncButtonLabel }}
            </button>
          </template>

          <template v-if="!hasChanges && isValidOutline && (!statusIsCompleted || (statusIsCompleted && hasNewData()))">
            <app-button v-if="!aiEnabled"
              theme="primary"
              class=""
              type="submit"
              title="AI is not enabled for this account"
              disabled="disabled">
              {{ 'Generate Course' }}
            </app-button>
            <app-button v-else-if="aiEnabled"
              theme="ai-primary"
              class=""
              type="submit"
              :disabled="disabled"
              @click="checkGenerateCourseConfirmation()">
              <svg class="element-icon--ai element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-ai"></use></svg>
              {{ disabled ? 'Processing...' : (statusIsCompleted ? 'Generate New Screens' : 'Generate Course') }}
            </app-button>
          </template>

          <!-- <app-button
            theme="secondary"
            :disabled="disabled"
            @click="downloadOutline()">
            Download
          </app-button> -->
        </div>
      </div>
    </div>
    <div class="page-main__content">

      <app-error class="is--inline"
        v-if="hasErrors"
        v-bind="{ errors }" />

      <div class="page-main__content" v-if="!statusIsCompleted">
        <div class="app-toast is--ai is--inline">
          <p class="app-toast__title">Review the course outline generated from your course details and Golden Source materials. If desired, you may edit and select Regenerate Outline to regenerate the outline.</p>
          <div class="app-toast__buttons">
            <app-button v-if="!statusIsCompleted"
                @click="onRegenerateOutlinePressed()"
                class="element-button--text element-button">
                <svg class="element-icon--refresh element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-refresh"></use></svg>
                Regenerate Outline
            </app-button>
          </div>
        </div>
      </div>

      <div class="page-main__section--course-details page-main__section">
        <div class="page-main__section-header">
          <h3 class="page-main__section-title">Course Details</h3>
        </div>
        <div class="page-main__section-body grid">
          <div class="col-12">
            <div class="input text">
              <label for="name">Course Title (required)</label>
              <input type="text" class="form-control" name="name" id="name" v-model="details.name" required>
            </div>
          </div>
          <div class="col-12">
            <div class="input text">
              <label for="name">Course Description</label>
              <textarea rows="8" class="form-control" name="description" id="description" v-model="details.description"></textarea>
            </div>
          </div>

          <div :class="['app-accordion', 'col-12', detailsOpen ? 'is--open' : '']">
            <div class="app-accordion__header" @click="toggleDetails()">
              <h3 class="app-accordion__title">Additional Course Details</h3>
              <svg class="element-icon--chevron-down element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-chevron-down"></use></svg>
            </div>
            <div v-if="detailsOpen" class="app-accordion__body">
              <div class="grid">
                <div class="col-12 col-md-6">
                  <div class="input text">
                    <label for="name">Who is this course for?</label>
                    <input type="text" name="input_audience" id="input_audience" v-model="details.input_audience" disabled>
                  </div>
                </div>
                <div class="col-12 col-md-6">
                  <div class="input text">
                    <label for="name">Are there any particular types of activities or presentation methods you want to prioritize?</label>
                    <input type="text" name="input_level" id="input_level" v-model="details.input_level" disabled>
                  </div>
                </div>
                <div class="col-12">
                  <div class="input text">
                    <label for="name">What are your learning objectives?</label>
                    <textarea rows="8" name="input_extra_prompt" id="input_extra_prompt" v-model="details.input_extra_prompt" disabled></textarea>
                  </div>
                </div>
                <div class="col-12">
                  <label for="name" class="checkbox__group-label">Selected Golden Source</label>
                  <div v-if="allTopicsSelected">
                    <p class="element-instructions">All Golden Source topics were selected.</p>
                  </div>
                  <div v-else class="app-publication-ai-outline__golden-source">
                    <div v-for="topic in selectedTopics" :key="topic.uuid" class="element-badge">
                      <p class="element-badge__text">{{ topicByUUID(topic) }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="outline" class="page-main__section--course-outline page-main__section">
        <div class="page-main__section-header">
          <h3 class="page-main__section-title">
            {{ statusIsCompleted ? 'Course Content' : 'Course Outline' }}
            <div data-bs-toggle="dropdown" class="element-chip is--ai">
                <svg class="element-icon--ai element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-ai"></use></svg>
                AI
            </div>
            <div class="app-tooltip--generate-status app-tooltip dropdown-menu">
                <h4 class="app-tooltip__title">AI Generated</h4>
                <div class="app-tooltip__body">
                <p>We’ve used AI to generate this content based on the information provided.</p>
                </div>
            </div>
            <app-button v-if="!hasBasicAccess"
              class="element-button--secondary element-button"
              theme="clear"
              @click="addTopic()">
              <svg class="element-icon--delete element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-plus"></use></svg>Add Topic
            </app-button>
          </h3>
          <p class="element-instructions">Add, edit, delete, and reorder your generated course structure as necessary. Note that the summary is only used as prompts for AI to generate screen content, and will not be visible to the end-user.</p>
        </div>

        <Draggable
          v-model="outline.topics"
          handle=".topic-handle"
          draggable=".topic"
          :disabled="disabled || hasBasicAccess"
          @start="onTopicMoveStart"
          @end="onTopicMoveEnd"
        >
        <div v-for="(topic, topicIndex) in outline.topics" :key="`topic-${topicIndex}`" :class="['topic', 'app-accordion--outline-topic','app-accordion', isItemOpen(topicIndex) ? 'is--open' : '']">
          <!-- Topic Section -->
          <div class="app-accordion__header">
              <svg class="topic-handle element-icon--draggable element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-draggable"></use></svg>
            <div class="element-button--icon element-button">
              <svg @click="toggleItem(topicIndex)" class="element-icon--chevron-down element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-chevron-down"></use></svg>
            </div>
            <template v-if="!isEditingTopic(topicIndex)">
              <div @click="toggleItem(topicIndex)" class="app-accordion__titles">
                <h4 class="app-accordion__title">Topic: {{ topic.topicName }} <span v-if="isItemMoved(topicIndex)" class="element-badge is--alt">Moved</span></h4>
                <p v-if="showSummary && !statusIsCompleted">{{ topic.topicSummary }}</p>
              </div>
              <app-button v-if="!hasBasicAccess"
                class="element-button--secondary element-button"
                theme="clear"
                @click="addLearningObject(topicIndex)">
                <svg class="element-icon--delete element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-plus"></use></svg>Add Learning Object
              </app-button>
              <span v-if="statusIsCompleted && !topic.id" class="element-badge is--success">Not Yet Generated</span>
              <app-button
                class="element-button--icon element-button"
                theme="clear"
                @click="editTopic(topicIndex)">
                <svg class="element-icon--delete element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-edit"></use></svg>
              </app-button>
              <template v-if="!hasBasicAccess">
                <div @click="$event.isAction = true" class="dropdown">
                  <button class="element-table__actions-button element-button--icon element-button" type="button" data-bs-toggle="dropdown">
                    <svg class="element-icon--more-info element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-more-info"></use></svg>
                  </button>
                  <div class="app-dropdown dropdown-menu dropdown-menu-right">
                    <app-button v-if="!hasBasicAccess"
                      class="element-button--icon element-button"
                      theme="clear"
                      @click="deleteTopic(topicIndex)">
                      <svg class="element-icon--delete element-icon" width="1em" height="1em">
                        <use href="/img/icons.svg#icon-delete"></use>
                      </svg> Delete
                    </app-button>
                  </div>
                </div>
              </template>
            </template>
            <template v-else>
              <div class="app-publication-ai-outline__editing">
                <div class="input text">
                  <label>Topic Name</label>
                  <input type="text" v-model="topic.topicName" placeholder="Topic Name">
                </div>
                <div v-if="!statusIsCompleted || !topic.id" class="input textarea">
                  <label>Topic Summary</label>
                  <textarea v-model="topic.topicSummary" placeholder="Edit topic summary"></textarea>
                </div>
              </div>
              <app-button
                class="element-button--secondary element-button"
                theme="clear"
                @click="saveTopic(topicIndex)">
                Done
              </app-button>
              <app-button v-if="!hasBasicAccess"
                class="element-button--icon element-button"
                theme="clear"
                @click="deleteTopic(topicIndex)">
                <svg class="element-icon--delete element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-delete"></use></svg>
              </app-button>
            </template>
          </div>

          <div class="learning-objects app-accordion__body" v-if="isItemOpen(topicIndex)">
            <!-- Learning Objects Section -->
            <Draggable
              v-model="topic.learningObjects"
              handle=".object-handle"
              draggable=".learning-object"
              :disabled="disabled || hasBasicAccess"
              @start="onLearningObjectMoveStart($event, topicIndex)"
              @end="onLearningObjectMoveEnd($event, topicIndex)"
            >
            <div v-for="(learningObject, loIndex) in topic.learningObjects" :key="loIndex" :class="['learning-object', 'app-accordion--outline-lo', 'app-accordion', isItemOpen(topicIndex, loIndex) ? 'is--open' : '']">
              <div class="app-accordion__header">
                <svg class="object-handle element-icon--draggable element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-draggable"></use></svg>
                <div class="element-button--icon element-button">
                  <svg @click="toggleItem(topicIndex, loIndex)" class="element-icon--chevron-down element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-chevron-down"></use></svg>
                </div>
                <template v-if="!isEditingLearningObject(topicIndex, loIndex)">
                  <div @click="toggleItem(topicIndex, loIndex)" class="app-accordion__titles">
                    <h5 class="app-accordion__title">Learning Object: {{ learningObject.learningObjectName }} <span v-if="isItemMoved(topicIndex, loIndex)" class="element-badge is--accent-03">Moved</span></h5>
                    <p v-if="showSummary && !statusIsCompleted">{{ learningObject.learningObjectSummary }}</p>
                  </div>
                  <app-button v-if="!hasBasicAccess"
                    class="element-button--secondary element-button"
                    theme="clear"
                    @click="addScreen(topicIndex, loIndex)">
                    <svg class="element-icon--plus element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-plus"></use></svg>Add Screen
                  </app-button>
                  <span v-if="statusIsCompleted && !learningObject.id" class="element-badge is--success">Not Yet Generated</span>
                  <app-button
                    class="element-button--icon element-button"
                    theme="clear"
                    @click="editLearningObject(topicIndex, loIndex)">
                    <svg class="element-icon--edit element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-edit"></use></svg>
                  </app-button>

                  <template v-if="!hasBasicAccess">
                    <div @click="$event.isAction = true" class="dropdown">
                      <button class="element-table__actions-button element-button--icon element-button" type="button" data-bs-toggle="dropdown">
                        <svg class="element-icon--more-info element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-more-info"></use></svg>
                      </button>
                      <div class="app-dropdown dropdown-menu dropdown-menu-right">
                        <app-button v-if="!hasBasicAccess"
                          class="element-button--icon element-button"
                          theme="clear"
                          @click="onMoveLearningObjectPressed(topicIndex, loIndex)"
                          >
                          <svg class="element-icon--arrow-right-circle element-icon" width="1em" height="1em">
                            <use href="/img/icons.svg#icon-arrow-right-circle"></use>
                          </svg> Move Learning Object
                        </app-button>
                        <app-button v-if="!hasBasicAccess"
                          class="element-button--icon element-button"
                          theme="clear"
                          @click="deleteLearningObject(topicIndex, loIndex)">
                          <svg class="element-icon--delete element-icon" width="1em" height="1em">
                            <use href="/img/icons.svg#icon-delete"></use>
                          </svg> Delete
                        </app-button>
                      </div>
                    </div>
                  </template>
                </template>
                <template v-else>
                  <div class="app-publication-ai-outline__editing">
                    <div class="input text">
                      <label>Learning Object Name</label>
                      <input type="text" class="form-control" v-model="learningObject.learningObjectName" placeholder="Learning Object Name">
                    </div>
                    <div v-if="!statusIsCompleted || !learningObject.id" class="input textarea">
                      <label>Learning Object Summary</label>
                      <textarea v-model="learningObject.learningObjectSummary" placeholder="Edit learning object summary"></textarea>
                    </div>
                  </div>
                  <app-button
                    class="element-button--secondary element-button"
                    theme="clear"
                    @click="saveLearningObject(topicIndex, loIndex)">
                    Done
                  </app-button>
                  <app-button v-if="!hasBasicAccess"
                    class="element-button--icon element-button"
                    theme="clear"
                    @click="deleteLearningObject(topicIndex, loIndex)">
                    <svg class="element-icon--delete element-icon" width="1em" height="1em">
                      <use href="/img/icons.svg#icon-delete"></use>
                    </svg>
                  </app-button>
                </template>
              </div>
              <div class="screens app-accordion__body" v-if="isItemOpen(topicIndex, loIndex)">
                <!-- Screens Section -->
                <Draggable
                  v-model="learningObject.screens"
                  handle=".screen-handle"
                  draggable=".screen"
                  :disabled="disabled || hasBasicAccess"
                  @start="onScreenMoveStart($event, topicIndex, loIndex)"
                  @end="onScreenMoveEnd($event, topicIndex, loIndex)"
                >
                <div v-for="(screen, screenIndex) in learningObject.screens" :key="screenIndex" class="screen app-publication-ai-outline__screen">
                  <svg class="screen-handle element-icon--draggable element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-draggable"></use></svg>
                  <template v-if="!isEditingScreen(topicIndex, loIndex, screenIndex)">
                    <div class="app-publication-ai-outline__screen-titles">
                      <h6 class="app-publication-ai-outline__screen-title">
                        {{ screen.screenName }}
                        <span class="element-badge is--accent-03">{{ screen.screenAlias }}</span>
                        <span v-if="isItemMoved(topicIndex, loIndex, screenIndex)" class="element-badge is--accent-03">Moved</span>
                      </h6>
                      <div class="app-publication-ai-outline__screen-description" v-if="showSummary && (!statusIsCompleted || !screen.id)">{{ screen.screenSummary }}</div>
                    </div>
                    <span v-if="statusIsCompleted && !screen.id" class="element-badge is--success">Not Yet Generated</span>
                    <app-button
                      class="element-button--icon element-button"
                      theme="clear"
                      @click="editScreen(topicIndex, loIndex, screenIndex)">
                      <svg class="element-icon--edit element-icon" width="1em" height="1em">
                        <use href="/img/icons.svg#icon-edit"></use>
                      </svg>
                    </app-button>
                    <template v-if="!hasBasicAccess">
                      <div @click="$event.isAction = true" class="dropdown">
                        <button class="element-table__actions-button element-button--icon element-button" type="button" data-bs-toggle="dropdown">
                          <svg class="element-icon--more-info element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-more-info"></use></svg>
                        </button>
                        <div class="app-dropdown dropdown-menu dropdown-menu-right">
                          <app-button v-if="!hasBasicAccess"
                            class="element-button--icon element-button"
                            theme="clear"
                            @click="onMoveScreenPressed(topicIndex, loIndex, screenIndex)"
                            >
                            <svg class="element-icon--arrow-right-circle element-icon" width="1em" height="1em">
                              <use href="/img/icons.svg#icon-arrow-right-circle"></use>
                            </svg> Move Screen
                          </app-button>
                          <app-button v-if="!hasBasicAccess"
                            class="element-button--icon element-button"
                            theme="clear"
                            @click="deleteScreen(topicIndex, loIndex, screenIndex)">
                            <svg class="element-icon--delete element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-delete"></use></svg>
                            Delete
                            </app-button>
                        </div>
                      </div>
                    </template>
                  </template>
                  <template v-else>
                    <div class="app-publication-ai-outline__editing grid">
                      <div class="col-12 col-lg-8">
                        <div class="input text">
                          <label>Screen Name</label>
                          <input type="text" v-model="screen.screenName" placeholder="Screen Name">
                        </div>
                      </div>
                      <div v-if="!statusIsCompleted || !screen.id" class="col-12 col-lg-4">
                        <label class="select2-label">Screen Type</label>
                        <app-select
                          :groups="screenTypeChoices"
                          :value="screen.screenAlias"
                          v-model="screen.screenAlias">
                        </app-select>
                      </div>
                      <div v-if="!statusIsCompleted || !screen.id" class="col-12">
                        <div class="input textarea">
                          <label>Screen Summary</label>
                          <textarea v-model="screen.screenSummary" placeholder="Edit screen summary"></textarea>
                        </div>
                      </div>
                    </div>
                    <app-button v-if="screen.id"
                      class="element-button--secondary element-button"
                      theme="clear"
                      @click="editScreenContent(screen, learningObject)">
                      Edit Content
                    </app-button>
                    <app-button
                      class="element-button--secondary element-button"
                      theme="clear"
                      @click="saveScreen(topicIndex, loIndex, screenIndex)">
                      Done
                    </app-button>
                  </template>
                </div>
                </Draggable>
              </div>
            </div>
            </Draggable>
          </div>
        </div>
        </Draggable>
      </div>
    </div>
    <learning-object-rearrangement-overlay v-if="showLearningObjectRearrangementOverlay"
      :publication="publication"
      :topicSource="outline.topics"
      :activeTopic="activeTopic"
      :activeLearningObject="activeLearningObject"
      @cancel="onContentOverlayClose()"
      @moveLearningObject="onMoveLearningObject">
    </learning-object-rearrangement-overlay>
    <screen-rearrangement-overlay v-if="showScreenRearrangementOverlay"
      :publication="publication"
      :topicSource="outline.topics"
      :activeTopic="activeTopic"
      :activeLearningObject="activeLearningObject"
      :activeScreen="activeScreen"
      @cancel="onContentOverlayClose()"
      @moveScreen="onMoveScreen">
    </screen-rearrangement-overlay>
    <generate-course-confirmation-overlay v-if="showGenerateCourseConfirmationOverlay"
      :disabled="disabled"
      @cancel="onGenerateCourseConfirmationCancel()"
      @confirm="onGenerateCourseConfirmed()">
    </generate-course-confirmation-overlay>
  </div>
</template>

<script>
import _ from 'lodash'
import aiApi from '../api/ai'
import api from '../api/builder'
import publisher from '../api/publisher'
import moment from 'moment'
import { mapGetters } from 'vuex'
import { VueDraggableNext as Draggable } from 'vue-draggable-next'
import LearningObjectRearrangementOverlay from './LearningObjectRearrangementOverlay'
import ScreenRearrangementOverlay from './ScreenRearrangementOverlay'
import GenerateCourseConfirmationOverlay from './GenerateCourseConfirmationOverlay'
import { localizeDate } from '../helpers/util'
import { mapState } from 'vuex'
import Echo from '../helpers/echo'

export default {
  components: {
    Draggable,
    LearningObjectRearrangementOverlay,
    ScreenRearrangementOverlay,
    GenerateCourseConfirmationOverlay,
  },
  props: {
    project: {
      type: Object,
    },
    source: {
      type: Object,
    },
    sourceType: {
      type: String,
      default: 'publication'
    },
    screenSource: {
      type: Object,
    },
    aiEnabled: {
      type: [Boolean, Number],
      default: false
    },
    isIntegrated: {
      type: [Boolean, Number],
      default: false
    },
    domains: {
      type: Array,
      default: () => ([])
    },
  },
  data() {
    return {
      publication: this.source || {},
      screenList: _.cloneDeep(this.screenSource) || {},
      outline: _.cloneDeep(this.source?.ai_preferences?.outline) || {}, // Perform deep clone for correct comparison
      jobQueue: this.source?.ai_preferences?.jobStatus?.jobQueue || [],
      details: {
        name: this.source.name || '',
        description: this.source.description || '',
        input_audience: this.source.ai_preferences?.input_audience || '',
        input_level: this.source.ai_preferences?.input_level || '',
        input_extra_prompt: this.source.ai_preferences?.input_extra_prompt || '',
      },
      showSummary: true,
      objState:  _.cloneDeep(this.source?.ai_preferences?.outline) || {},
      deletedIds: this.resetDeletedIds(),
      disabled: false,
      errors: null,
      errorMessage: null,
      activeLearningObjectId: null, // Used to track the learning object being edited
      detailsOpen: false,
      showLearningObjectRearrangementOverlay: false,
      showScreenRearrangementOverlay: false,
      moveFrom: null,
      activeTopic: null,
      activeLearningObject: null,
      activeScreen: null,
      showInfo: false,
      showGenerateCourseConfirmationOverlay: false,
    }
  },
  computed: {
    ...mapState('builder', {
      statusLog: state => state.statusLog,
      users: state => state.users,
      isSyncing: state => state.isSyncing,
      syncStatus: state => state.status
    }),
    ...mapGetters('builder', [
      'screenTypes',
      'interactions',
      'changes'
    ]),
    ...mapGetters([
      'hasBasicAccess'
    ]),
    isValidOutline() {
      // TODO: validate
      return this.outline.topics && this.outline.topics.length > 0
    },
    hasErrors () {
      return this.errorMessage || (this.errors?.message ?? this.errors?.errors?.message)
    },
    hasChanges() {
      const org = this.publication.ai_preferences?.outline
      const eq = _.isEqual(org, this.outline)

      if (this.details.name !== this.publication.name || (this.details.description || null) !== (this.publication.description || null))
        return true

      return !eq
    },
    statusIsCompleted() {
      return this.getStatus() === 'completed'
    },
    screenTypeChoices () {
      const result = _.map(this.screenTypes, (types, category) => ({
        id: category,
        text: category.toUpperCase(),
        children: _.map(types, (screen, index) => ({
          id: screen.name,
          text: screen.name,
          value: screen.name,
          customProperties: { category, type: screen.type }
        }))
      }))
      return result
    },
    defaultScreenType () {
      const defaultScreenType = 'Presentation - Standarsd' // default to presentation standard, fallback to first item in the list
      const screenType = _.find(this.interactions, (screen) => screen.name === defaultScreenType)
      if (screenType)
        return screenType.name
      return this.interactions[0].name
    },
    isSourceOutdated () {
        // if synced_at not null, check for outdated flag
        return _.get(this.source, 'synced_at') ? _.get(this.source, 'outdated', false) : false;
    },
    canSync () {
        // has ingegration and not in screen template edit page
        return this.isIntegration && this.statusIsCompleted && !this.hasNewData()
    },
    syncButtonLabel () {
        return this.isSyncing ? 'Syncing to Portal...' : 'Sync to Portal'
    },
    isIntegration () {
        return !!_.get(this.source, 'integration_uuid', false)
    },
    topics () {
      let topics = []
      _.each(this.domains, (domain) => {
        _.each(domain.topics, (topic) => {
          topics.push(topic)
        })
      })
      return topics
    },
    selectedTopics () {
      return this.source.ai_preferences?.input_topics || []
    },
    allTopicsSelected () {
      return this.topics.length > 0 && (this.selectedTopics.length === 0 || this.selectedTopics.length === this.topics.length);
    },
    currentUser () {
      return this.$store.state.user
    },
    userCount () {
      return this.users.length
    },
  },
  methods: {
    localizeDate: localizeDate,
    async onSavePressed () {
        await this.$store.dispatch('builder/commitChanges')
    },
    async onSyncPressed () {
      this.$store.commit('builder/setStatus', 'syncing');
      let result = await api.syncPublication(this.source.id)
      this.$store.commit('builder/setIsSyncing', true)
      this.$store.commit('builder/clearStatus');
    },
    formatJsDate(date) {
      return moment(date).format('MMM DD, YYYY h:mm A')
    },
    getStatus() {
      return _.get(this.publication, 'ai_preferences.status', "new")
    },
    hasNewData() {
      // Check if there are any new data that needs to be saved
      var $newData = false
      this.outline.topics.forEach((topic) => {
        if (!topic.id) {
          $newData = true // Found a topic that is not marked for deletion
          return true
        }
        topic.learningObjects.forEach((learningObject) => {
          if (!learningObject.id) {
            $newData = true // Found a topic that is not marked for deletion
            return true
          }

          learningObject.screens.forEach((screen) => {
            if (!screen.id) {
              $newData = true // Found a topic that is not marked for deletion
              return true
            }
            else
            {
              let matchedScreen = this.getDataObjectById(screen.id)
              if (!matchedScreen) {
                $newData = true
                return true
              }

              this.jobQueue.forEach((job => {
                if (job.id === screen.id && job.complete === false) {
                  $newData = true // Found a job that is not marked for deletion
                  return true
                }
              }))
            }
          })
        })
      })
      console.log('HAS NEW DATA:', $newData)
      return $newData
    },
    onPreviewPressed() {
      window.open(this.publication.preview_url, '_blank')
    },
    onDownloadPressed() {
      window.open(`/publications/${this.publication.id}/download-outline`, '_blank')
    },
    toggleSummaries() {
      this.showSummary = !this.showSummary
    },
    toggleItem(topicIndex, loIndex) {
      if (loIndex !== undefined)
        this.objState.topics[topicIndex].learningObjects[loIndex].open = !this.isItemOpen(topicIndex, loIndex)
      else
        this.objState.topics[topicIndex].open = !this.isItemOpen(topicIndex, loIndex)
    },
    isItemOpen(topicIndex, loIndex) {
      if (loIndex !== undefined) 
        return this.objState?.topics?.[topicIndex]?.learningObjects?.[loIndex]?.open === true || this.objState?.topics?.[topicIndex]?.learningObjects?.[loIndex]?.open === undefined
      return this.objState?.topics?.[topicIndex]?.open === true || this.objState?.topics?.[topicIndex]?.open === undefined
    },
    toggleTopicMenu(topicIndex) {
      this.objState.topics.forEach((topic, index) => {
        if (index === topicIndex) {
          topic.menuOpen = !topic.menuOpen
        } else {
          topic.menuOpen = false
        }
      })
    },
    isItemMoved(topicIndex, loIndex, screenIndex) {
      if (screenIndex !== undefined) 
        return this.objState?.topics?.[topicIndex]?.learningObjects?.[loIndex]?.screens[screenIndex]?.moved === true
      if (loIndex !== undefined) 
        return this.objState?.topics?.[topicIndex]?.learningObjects?.[loIndex]?.moved === true
      return this.objState?.topics?.[topicIndex]?.moved === true
    },
    isTopicMenuOpen(topicIndex) {
      return this.objState.topics[topicIndex]?.menuOpen === true
    },
    addTopic(index) {
      index = index || this.outline.topics.length - 1
      const _newItem = {
        topicName: '',
        topicSummary: '',
        learningObjects: [
          {
            learningObjectName: '',
            learningObjectSummary: '',
            screens: [
              {
                screenName: '',
                screenAlias: this.defaultScreenType,
                screenSummary: ''
              }
            ]
          }
        ],
      }
      this.outline.topics.splice(index + 1, 0, _newItem)

      let _newStateObject = _.cloneDeep(_newItem)
      _newStateObject.editing = true
      this.objState.topics.splice(index + 1, 0, _newStateObject)
    },
    editTopic(topicIndex) {
      this.objState.topics[topicIndex].editing = !this.objState.topics[topicIndex].editing
    },
    isEditingTopic(topicIndex) {
      return this.objState.topics[topicIndex].editing
    },
    saveTopic(topicIndex) {
      this.objState.topics[topicIndex].editing = false
    },
    deleteTopic(topicIndex) {
      if (!confirm('Are you sure you want to delete this topic?'))
        return
      this.markForDeletion(this.outline.topics[topicIndex], 'topics')

      this.outline.topics[topicIndex].learningObjects.forEach((learningObject, index) => {
        this.deleteLearningObject(topicIndex, index, true)
      })

      this.outline.topics.splice(topicIndex, 1)
      this.objState.topics.splice(topicIndex, 1)
    },
    addLearningObject(topicIndex, index) {
      index = index || this.outline.topics[topicIndex].learningObjects.length - 1
      const _newItem = {
        learningObjectName: '',
        learningObjectSummary: '',
        screens: [
          {
            screenName: '',
            screenAlias: this.defaultScreenType,
            screenSummary: ''
          }
        ]
      }
      this.outline.topics[topicIndex].learningObjects.splice(index + 1, 0, _newItem)

      let _newStateObject = _.cloneDeep(_newItem)
      _newStateObject.editing = true
      this.objState.topics[topicIndex].learningObjects.splice(index + 1, 0, _newStateObject)
    },
    editLearningObject(topicIndex, loIndex) {
      this.objState.topics[topicIndex].learningObjects[loIndex].editing = !this.objState.topics[topicIndex].learningObjects[loIndex].editing
    },
    isEditingLearningObject(topicIndex, loIndex) {
      return this.objState.topics[topicIndex].learningObjects[loIndex].editing
    },
    saveLearningObject(topicIndex, loIndex) {
      this.objState.topics[topicIndex].learningObjects[loIndex].editing = false
    },
    deleteLearningObject(topicIndex, loIndex, parentDeleted = false) {
      if (!parentDeleted && !confirm('Are you sure you want to delete this learning object?'))
        return
      this.markForDeletion(this.outline.topics[topicIndex].learningObjects[loIndex], 'learningObjects')
      
      this.outline.topics[topicIndex].learningObjects[loIndex].screens.forEach((screen, index) => {
        this.deleteScreen(topicIndex, loIndex, index, true)
      })

      this.outline.topics[topicIndex].learningObjects.splice(loIndex, 1)
      this.objState.topics[topicIndex].learningObjects.splice(loIndex, 1)
    },
    addScreen(topicIndex, loIndex, index) {
      index = index || this.outline.topics[topicIndex].learningObjects[loIndex].screens.length - 1
      const _newItem = {
        screenName: '',
        screenAlias: this.defaultScreenType,
        screenSummary: ''
      }
      this.outline.topics[topicIndex].learningObjects[loIndex].screens.splice(index + 1, 0, _newItem)

      let _newStateObject = _.cloneDeep(_newItem)
      _newStateObject.editing = true
      this.objState.topics[topicIndex].learningObjects[loIndex].screens.splice(index + 1, 0, _newStateObject)
    },
    editScreen(topicIndex, loIndex, screenIndex) {
      this.objState.topics[topicIndex].learningObjects[loIndex].screens[screenIndex].editing = !this.objState.topics[topicIndex].learningObjects[loIndex].screens[screenIndex].editing
    },
    editScreenContent(screen, learningObject) {
      window.location.href = `/learning/${learningObject.id}/edit?publication=${this.publication.id}#${screen.id}`
    },
    getDataObjectById(id) {
      // Find the screen by ID in the screenList
      var matchedScreen = null;
      this.screenList.forEach(learningObject => {
        learningObject.data_objects.forEach(screen => {
          if (screen.id === id) {
            matchedScreen = screen
            this.activeLearningObjectId = learningObject.id // Set the active learning object ID
            return
          }
        })
        if (matchedScreen) return
      })
      return matchedScreen
    },
    setDataObjectById(id, updatedScreen) {
      // Update the screen in the screenList by ID
      this.screenList.forEach(learningObject => {
        learningObject.data_objects.forEach((screen, index) => {
          if (screen.id === id) {
            learningObject.data_objects[index] = updatedScreen
            return
          }
        })
      })
    },
    isEditingScreen(topicIndex, loIndex, screenIndex) {
      return this.objState.topics[topicIndex].learningObjects[loIndex].screens[screenIndex].editing
    },
    saveScreen(topicIndex, loIndex, screenIndex) {
      this.objState.topics[topicIndex].learningObjects[loIndex].screens[screenIndex].editing = false
    },
    deleteScreen(topicIndex, loIndex, screenIndex, parentDeleted = false) {
      if (!parentDeleted && !confirm('Are you sure you want to delete this screen?'))
        return

      this.markForDeletion(this.outline.topics[topicIndex].learningObjects[loIndex].screens[screenIndex], 'screens')

      this.outline.topics[topicIndex].learningObjects[loIndex].screens.splice(screenIndex, 1)
      this.objState.topics[topicIndex].learningObjects[loIndex].screens.splice(screenIndex, 1)
    },
    onTopicMoveStart(e) {
      this.resetOpenStates()
    },
    onTopicMoveEnd(e) {
      // Update objState to match the new order in outline
      const movedItem = this.objState.topics.splice(e.oldIndex, 1)[0]
      this.objState.topics.splice(e.newIndex, 0, movedItem)
    },
    onLearningObjectMoveStart(e, topicIndex) {
      this.resetOpenStates(topicIndex)
    },
    onLearningObjectMoveEnd(e, topicIndex){
      const movedItem = this.objState.topics[topicIndex].learningObjects.splice(e.oldIndex, 1)[0]
      this.objState.topics[topicIndex].learningObjects.splice(e.newIndex, 0, movedItem)
    },
    onScreenMoveStart(e, topicIndex, loIndex) {
      this.resetOpenStates(topicIndex, loIndex)
    },
    onScreenMoveEnd(e, topicIndex, loIndex){
      const screens = this.objState.topics[topicIndex].learningObjects[loIndex].screens;
      const editingIndexes = screens
        .map((screen, idx) => screen.editing ? idx : -1)
        .filter(idx => idx !== -1);
      const [movedItem] = screens.splice(e.oldIndex, 1);
      screens.splice(e.newIndex, 0, movedItem);
      screens.forEach(screen => { screen.editing = false });
      // neccessary to ensure editing states are preserved after reordering so it doesnt have a visual data display mismatch
      this.$nextTick(() => {
        editingIndexes.forEach(idx => {
          if (screens[idx]) screens[idx].editing = true;
        });
      });
    },
    resetOpenStates(topicIndex, loIndex) {
      this.objState.topics.forEach((topic, _tIndex) => {

        if (topicIndex !== undefined) { // collapsing objects
          if (_tIndex === topicIndex) // collabose a single topic
          {
            topic.learningObjects.forEach((learningObject, _loIndex) => {

              if (loIndex !== undefined) { // collapsing screens
                if (_loIndex === loIndex)
                  learningObject.screens.forEach((screen, _sIndex) => {
                    screen.open = false
                  })
              }
              else
                learningObject.open = false // collapse learning objects
            })
          }
        }
        else
          topic.open = false // collapse all topics
      })
    },
    resetDeletedIds() {
      return {
        'topics': [],
        'learningObjects': [],
        'screens': []
      }
    },
    onContentOverlayClose() {
      this.activeScreen = null
      this.moveFrom = null
      this.activeLearningObjectId = null // Reset active learning object ID
      this.showLearningObjectRearrangementOverlay = false
      this.showScreenRearrangementOverlay = false
      this.activeLearningObject = null
    },
    onMoveLearningObjectPressed(topicIndex, loIndex) {
      this.moveFrom = {
        topicIndex: topicIndex,
        loIndex: loIndex,
      }

      this.showLearningObjectRearrangementOverlay = true
      this.activeTopic = topicIndex
      this.activeLearningObject = loIndex
    },
    onMoveLearningObject(destinationTopicIndex, loId) {
      // set values from source when move learning object was pressed
      const sourceTopicIndex = this.moveFrom.topicIndex
      const sourceLoIndex = this.moveFrom.loIndex

      if (sourceTopicIndex === -1 || destinationTopicIndex === -1) {
        console.error('Could not find source or destination topic')
        return;
      }

      if (sourceLoIndex === -1) {
        console.error('Could not find learning object in source topic')
        return;
      }

      // Mark topics and learning object as changed
      const [movedLO] = this.outline.topics[sourceTopicIndex].learningObjects.splice(sourceLoIndex, 1)
      this.outline.topics[destinationTopicIndex].learningObjects.push(movedLO);

      // Sync objState
      if (sourceTopicIndex > -1 && destinationTopicIndex > -1) {
        if (sourceLoIndex > -1) {
          const [movedObjStateLO] = this.objState.topics[sourceTopicIndex].learningObjects.splice(sourceLoIndex, 1)
          this.objState.topics[destinationTopicIndex].learningObjects.push(movedObjStateLO)
          movedObjStateLO.moved = true
        }
      }

      this.onContentOverlayClose();
    },
    onMoveScreenPressed(topicIndex, loIndex, screenIndex) {
      this.moveFrom = {
        topicIndex: topicIndex,
        loIndex: loIndex,
        screenIndex: screenIndex,
      }

      this.activeTopic = topicIndex
      this.activeLearningObject = loIndex
      this.activeScreen = loIndex
      this.showScreenRearrangementOverlay = true
    },
    onMoveScreen(destinationTopicIndex, destinationLoIndex, screenId) {
      // set values from source when move screen was pressed
      const sourceTopicIndex = this.moveFrom.topicIndex
      const sourceLoIndex = this.moveFrom.loIndex
      const screenIndex = this.moveFrom.screenIndex

      if (sourceTopicIndex === undefined) {
        console.error('Could not find the screen to move');
        return;
      }

      if (destinationTopicIndex === -1 || destinationLoIndex === -1) {
        console.error('Could not find destination topic or learning object');
        return;
      }

      // Move the screen
      const [movedScreen] = this.outline.topics[sourceTopicIndex].learningObjects[sourceLoIndex].screens.splice(screenIndex, 1)
      this.outline.topics[destinationTopicIndex].learningObjects[destinationLoIndex].screens.push(movedScreen)

      // Sync objState
      const [movedObjStateScreen] = this.objState.topics[sourceTopicIndex].learningObjects[sourceLoIndex].screens.splice(screenIndex, 1)
      this.objState.topics[destinationTopicIndex].learningObjects[destinationLoIndex].screens.push(movedObjStateScreen)
      movedObjStateScreen.moved = true

      this.onContentOverlayClose();
    },
    markForDeletion(obj, type) {
      if (obj.id && !this.deletedIds[type].includes(obj.id)) {
        this.deletedIds[type].push(obj.id)
      }
    },
    async saveOutline() {
      // save the outline and update this.outline value

      // TODO: make api call to save publication outline then update publication variable
      const params = {
        id: this.publication.id,
        name: this.details.name,
        description: this.details.description,
        ai_preferences: {
          outline: this.outline,
        },
        deletedIds: this.deletedIds,
      }

      // api call to trigger genration job
      const result = await publisher.saveOutline(this.publication.id, params)

      if (result.errors) {
        // catch errors
        this.disabled = false
        this.errors = result.errors
        this.errorMessage = result.message
      } else {
        this.deletedIds = this.resetDeletedIds() // reset deleted ids after saving
        this.publication = result
        this.outline = _.cloneDeep(this.publication.ai_preferences?.outline)
        this.objState = _.cloneDeep(this.publication.ai_preferences?.outline)
        this.errors = this.errorMessage = null
      }
    },
    updateScreenContent(_screen) {
      this.setDataObjectById(_screen.id, _screen)
      this.onContentOverlayClose()
    },
    checkGenerateCourseConfirmation() {
      this.showGenerateCourseConfirmationOverlay = true
    },
    onGenerateCourseConfirmationCancel() {
      this.showGenerateCourseConfirmationOverlay = false
    },
    async onGenerateCourseConfirmed() {
      await this.generateCourse()
    },
    async generateCourse() {

      this.disabled = true

      let params = {}

      // api call to trigger genration job
      const result = await aiApi.generateFullCourse(this.publication.id, params)
      if (result.errors) {
        // catch errors
        this.disabled = false
        this.errors = result.errors
        this.errorMessage = result.message
      } else {
        // redirect
        window.location.href = '/projects/' + this.project.id + '/courses'
      }
    },
    onRegenerateOutlinePressed() {
      window.location.href = `/publications/${this.publication.id}/course-sources`
    },
    topicByUUID(uuid) {
      const topic = this.topics.find(topic => topic.uuid === uuid)
      return topic?.name || null
    },
    toggleDetails() {
      this.detailsOpen = !this.detailsOpen
    },
    toggleInfo () {
      this.showInfo = !this.showInfo
    },
  },
  watch: {
    async statusLog (value) {
      await this.$nextTick()
      this.$refs['log-status'].scrollTop = this.$refs['log-status'].scrollHeight
    },
  },
  created() {
    // set project to include interactions attribute, used to retrieve screen type dropdown selection
    this.project.interactions = this.project.driver.screenMapLite

    this.$store.dispatch('builder/initialize', {
      source: this.source,
      settings: this.settings,
      type: 'publication'
    })

    // join presence channel
    console.info(`Joining Presence Channel: ${this.$store.getters['builder/presenceChannel']}`)
    Echo.join(this.$store.getters['builder/presenceChannel'])
      .here((users) => {
        this.$store.commit('builder/setUsers', users)
      })
      .joining((user) => {
        this.$store.commit('builder/addUser', user)
      })
      .leaving((user) => {
        this.$store.commit('builder/removeUser', user)
      })

    // join notification channel
    console.info(`Joining Notification Channel: ${this.$store.getters['builder/notificationChannel']}`)
    Echo.private(this.$store.getters['builder/notificationChannel'])
      .notification((notification) => {
        this.$store.dispatch('builder/processNotification', notification)
      })

    this.$store.commit('builder/setSource', this.project)
  },
  mounted() {
    window._aiMetadata = {
      projectId: this.project.id ?? null,
      publicationId: this.publication.id ?? null,
      sessionId: this.publication?.ai_preferences?.sessionId ?? null,
      isLite: true
    }
  }
}
</script>